import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContent, Spinner, Button, ButtonIcon, Row, Pagination, Toggle } from 'components';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import { formatDate } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadIpmaMeetingApplicationsCreator,
  setIpmaMeetingApplicationsPageCreator,
  setIpmaMeetingApplicationsSortCreator,
  setIpmaMeetingApplicationsFiltersCreator,
  setIpmaMeetingApplicationVerifiedCreator,
} from 'store/actionsCreators';
import { describeSubmitterShort } from 'containers/ma/Meetings/Application/submitter';
import { flagKeys, flagColumnKeys } from 'containers/ma/Meetings/Application/flag';
import MeetingsTable, { ApplicantCell, StatusNote } from 'containers/ma/Meetings/Meetings.styled';
import CisContainer from '../../../../components/CisContainer/CisContainer.styled';

/** The list names the month a meeting is held in, not the exact day. */
const MONTH_FORMAT = 'MMMM YYYY';

/**
 * The applications one meeting type received, as seen in the green profile.
 *
 * Both meeting types use this screen; they are separate entries in the
 * navigation only to keep the two flows visually apart.
 */
const MeetingsList = ({
  meetingType,
  loading,
  list,
  loadList,
  setPage,
  setSort,
  setFilters,
  setVerified,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);

  // Permissions arrive after the first render, and until they do there is
  // nothing to deny yet - denying early would throw out a user who is allowed.
  const permissionsKnown = !!userPermissions;
  const canView =
    !permissionsKnown ||
    checkPermissions({ roles: [`ipma_meeting_view_${meetingType}`] }, userPermissions);

  // A user who may read the other meeting type is moved one tab along rather
  // than out of the section over a screen they cannot open. Failing that they
  // land on the attendance tab, which every green profile user may open.
  const allowedType = permissionsKnown
    ? ['cod', 'csp'].find((type) =>
        checkPermissions({ roles: [`ipma_meeting_view_${type}`] }, userPermissions),
      )
    : null;

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    canView,
    '/ipma',
    'page.ipma.meetings.accessDenied',
    `/ipma/meetings/${allowedType || 'attendance'}`,
  );

  useEffect(() => {
    if (document.getElementById('ipma-meetings-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    if (!allowed) return;

    setPage(1);
    loadList();
  }, [sort, allowed]);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    setSort(direction ? `${key}.${direction}` : null);
  };

  const statusOptions = [
    { value: 'SUBMITTED', label: t('page.ma.meetings.list.statusSubmitted') },
    { value: 'CHAIR_APPROVED', label: t('page.ma.meetings.list.statusChairApproved') },
    { value: 'ACCEPTED', label: t('page.ma.meetings.list.statusAccepted') },
    { value: 'REJECTED', label: t('page.ma.meetings.list.statusRejected') },
  ];

  // One column, two names for it: a COD table can hold invoiced seats, a CSP
  // one is about verified applications throughout.
  const columnFlag = flagColumnKeys(meetingType);
  const flagOptions = [
    { value: 'PAID', label: t(columnFlag.filterOn) },
    { value: 'UNPAID', label: t(columnFlag.filterOff) },
  ];

  // Recording the flag belongs to the people that answer the applications, so a
  // reader without that right sees the state without the switch.
  const canApprove = checkPermissions(
    { roles: [`ipma_meeting_approve_${meetingType}`] },
    userPermissions || [],
  );

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  return (
    <PageContent>
      <CisContainer>
        <MeetingsTable tablebreakpoint="lg" actionsWidth="10rem">
          <MeetingsTable.Head>
            <MeetingsTable.Row>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.memberAssociation || ''}
                  onFilter={(val) => handleColumnFilterChange('memberAssociation', val)}
                  filterPlaceholder={t('page.ipma.meetings.list.memberAssociationPlaceholder')}
                  label={t('page.ipma.meetings.list.memberAssociation')}
                  sort
                  sortDirection={getSortDirection('memberAssociation')}
                  onSort={(dir) => handleColumnSort('memberAssociation', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ipma.meetings.list.applicant')} isAction />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.name || ''}
                  onFilter={(val) => handleColumnFilterChange('name', val)}
                  filterPlaceholder={t('page.ma.meetings.list.namePlaceholder')}
                  label={t('page.ma.meetings.list.name')}
                  sort
                  sortDirection={getSortDirection('name')}
                  onSort={(dir) => handleColumnSort('name', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFrom || ''}
                  filterValueTo={filters.dateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateTo', val)}
                  filterPlaceholder={t('page.ma.meetings.list.datePlaceholder')}
                  label={t('page.ma.meetings.list.date')}
                  sort
                  sortDirection={getSortDirection('date')}
                  onSort={(dir) => handleColumnSort('date', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={statusOptions}
                  filterPlaceholder={t('page.ma.meetings.list.statusPlaceholder')}
                  label={t('page.ma.meetings.list.status')}
                  sort
                  sortDirection={getSortDirection('status')}
                  onSort={(dir) => handleColumnSort('status', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.verified || ''}
                  onFilter={(val) => handleColumnFilterChange('verified', val)}
                  filterOptions={flagOptions}
                  filterPlaceholder={t(columnFlag.placeholder)}
                  label={t(columnFlag.column)}
                  sort
                  sortDirection={getSortDirection('verified')}
                  onSort={(dir) => handleColumnSort('verified', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ma.meetings.list.actions')} isAction />
              </MeetingsTable.Heading>
            </MeetingsTable.Row>
          </MeetingsTable.Head>
          <MeetingsTable.Body>
            {loading ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={7}>
                  <Spinner />
                </MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={7}>{t('global.noResultsFound')}</MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <MeetingsTable.Row key={item.id}>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ipma.meetings.list.memberAssociation')}:
                      </MeetingsTable.Label>
                      {item.memberAssociation ? item.memberAssociation.name : null}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ipma.meetings.list.applicant')}:
                      </MeetingsTable.Label>
                      <ApplicantCell title={describeSubmitterShort(item.submittedBy) || undefined}>
                        {describeSubmitterShort(item.submittedBy) || '-'}
                      </ApplicantCell>
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t('page.ma.meetings.list.name')}:</MeetingsTable.Label>
                      {item.meeting ? item.meeting.name : null}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t('page.ma.meetings.list.date')}:</MeetingsTable.Label>
                      {item.meeting ? formatDate(item.meeting.date, MONTH_FORMAT) : null}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.status')}:
                      </MeetingsTable.Label>
                      {item.statusLabel}
                      {item.awaitsChair ? (
                        <StatusNote>
                          {t('page.ma.meetings.application.awaitsChairShort')}
                        </StatusNote>
                      ) : null}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t(columnFlag.column)}:</MeetingsTable.Label>
                      {/* The name of the flag follows the row, not the table: a
                          COD observer sitting between two invoiced seats is
                          verified rather than invoiced. */}
                      {/* The flag is the step after the decision, so it stays
                          shut - and says why - until IPMA accepted the
                          application. */}
                      {canApprove ? (
                        <Toggle
                          on={!!item.verified}
                          disabled={!item.verifiable}
                          title={
                            item.verifiable ? undefined : t('page.ipma.meetings.list.verifyBlocked')
                          }
                          custom={[t(flagKeys(item.flag).on), t(flagKeys(item.flag).off)]}
                          onClick={() =>
                            setVerified({
                              applicationId: item.id,
                              meetingType,
                              verified: !item.verified,
                            })
                          }
                        />
                      ) : (
                        t(item.verified ? flagKeys(item.flag).on : flagKeys(item.flag).off)
                      )}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.actions')}:
                      </MeetingsTable.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        <ButtonIcon
                          icon="eye"
                          secondary
                          as={Button}
                          title={t('page.ipma.meetings.list.view')}
                          onClick={() =>
                            history.push(`/ipma/meetings/${meetingType}/application/${item.id}`)
                          }
                        />
                      </Row>
                    </MeetingsTable.Cell>
                  </MeetingsTable.Row>
                ))
              : null}
          </MeetingsTable.Body>
        </MeetingsTable>
        <Pagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onChange={(payload) => {
            setPage(payload);
            loadList();
          }}
        />
      </CisContainer>
      {portalReady &&
        ReactDOM.createPortal(
          <Button
            style={{ width: 'auto' }}
            secondary
            onClick={() => history.push('/ipma/meetings/export')}
          >
            {t('page.ipma.meetings.list.export')}
          </Button>,
          document.getElementById('ipma-meetings-buttons-portal'),
        )}
    </PageContent>
  );
};

MeetingsList.propTypes = {
  meetingType: PropTypes.oneOf(['cod', 'csp']).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setVerified: PropTypes.func.isRequired,
};

MeetingsList.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, user }, { meetingType }) => ({
  loading: isLoading(loading, 'GLOBAL'),
  list: ipma.meetings[meetingType],
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch, { meetingType }) => ({
  loadList: () => dispatch(loadIpmaMeetingApplicationsCreator(meetingType)),
  setPage: (payload) => dispatch(setIpmaMeetingApplicationsPageCreator(meetingType, payload)),
  setSort: (payload) => dispatch(setIpmaMeetingApplicationsSortCreator(meetingType, payload)),
  setFilters: (payload) => dispatch(setIpmaMeetingApplicationsFiltersCreator(meetingType, payload)),
  setVerified: (payload) => dispatch(setIpmaMeetingApplicationVerifiedCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MeetingsList);
